import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { DocMode, JobLineInput } from '../../types/e2i';
import { formatAmount, lineAmount, numberText, parseNumber, round4 } from '../../utils/e2i';
import LineItemPicker from './LineItemPicker';

interface Props {
  open: boolean;
  line: JobLineInput | null;
  companyId: number;
  docMode: DocMode;
  currency: string;
  onClose: () => void;
  onSave: (line: Omit<JobLineInput, 'lineNum'>) => void;
}

/** A line of a job without a quotation: what is billed (one line per period or milestone, each invoiced once). */
const JobLineDialog: React.FC<Props> = ({ open, line, companyId, docMode, currency, onClose, onSave }) => {
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDescription(line?.description || '');
    setQuantity(line ? numberText(line.quantity) : '1');
    setUom(line?.uom || '');
    setUnitPrice(line ? numberText(line.unitPrice) : '');
    setTarget((docMode === 'Service' ? line?.account : line?.salesItem) || null);
    setTried(false);
    // Only when the dialog opens or shows another line: typing must not be reset by the page re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  const qty = parseNumber(quantity);
  const price = parseNumber(unitPrice);
  const problems: string[] = [];
  if (!description.trim()) problems.push('Type the description');
  if (qty === null || qty <= 0) problems.push('The quantity must be above 0');
  if (price === null || price < 0) problems.push('Type the unit price');

  const save = () => {
    setTried(true);
    if (problems.length || qty === null || price === null) return;
    const unit = round4(price);
    onSave({
      description: description.trim(),
      quantity: qty,
      uom: uom.trim() || null,
      unitPrice: unit,
      amount: lineAmount(qty, unit),
      salesItem: docMode === 'Service' ? line?.salesItem ?? null : target,
      account: docMode === 'Service' ? target : line?.account ?? null,
    });
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="e2i-job-line-title">
      <DialogTitle id="e2i-job-line-title">{line ? `Job line ${line.lineNum}` : 'Add a job line'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="e2i-job-line-desc"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 254))}
              fullWidth
              required
              multiline
              maxRows={3}
              helperText="Printed on the sales order and the invoice, e.g. a period or a milestone"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="e2i-job-line-qty" label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="e2i-job-line-uom" label="Unit" value={uom} onChange={(e) => setUom(e.target.value.slice(0, 30))} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="e2i-job-line-price" label={`Unit price ${currency}`.trim()} value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} inputProps={{ inputMode: 'decimal' }} fullWidth required />
          </Grid>
          <Grid item xs={12}>
            <LineItemPicker id="e2i-job-line-target" companyId={companyId} docMode={docMode} value={target} onChange={setTarget} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">Amount: <strong>{formatAmount(lineAmount(qty ?? 0, price ?? 0))} {currency}</strong></Typography>
          </Grid>
        </Grid>
        {tried && problems.length ? <Alert severity="warning" sx={{ mt: 2 }}>{problems.join('; ')}.</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button onClick={save}>{line ? 'Apply' : 'Add line'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default JobLineDialog;
